import React from 'react';
import Icon from '@/components/Icon';

/**
 * Общие компоненты ЛК родителя. Это «сырая» переработка дизайн-исходников
 * `design/parent-cabinet/Common.jsx` и `MarkBadge.jsx` — те же CSS-классы
 * (стили см. `./styles.css`), просто без `window.*` и под обычные React-импорты.
 */

// ── Иконка предмета ─────────────────────────────────────────────────
export function PCSubjectIcon({ subject, size = 32 }) {
  const colors = {
    math:  { bg: 'oklch(0.93 0.07 240)', fg: 'oklch(0.36 0.16 245)', glyph: '∑' },
    rus:   { bg: 'oklch(0.94 0.06 30)',  fg: 'oklch(0.42 0.14 30)',  glyph: 'Р' },
    lit:   { bg: 'oklch(0.93 0.07 70)',  fg: 'oklch(0.40 0.13 70)',  glyph: '📖' },
    eng:   { bg: 'oklch(0.93 0.07 290)', fg: 'oklch(0.40 0.16 290)', glyph: 'EN' },
    world: { bg: 'oklch(0.93 0.08 155)', fg: 'oklch(0.36 0.13 155)', glyph: '🌍' },
    art:   { bg: 'oklch(0.94 0.07 0)',   fg: 'oklch(0.42 0.14 0)',   glyph: '✎' },
    music: { bg: 'oklch(0.93 0.07 320)', fg: 'oklch(0.40 0.14 320)', glyph: '♪' },
    pe:    { bg: 'oklch(0.93 0.07 130)', fg: 'oklch(0.36 0.14 130)', glyph: '⚡' },
    tech:  { bg: 'oklch(0.93 0.06 50)',  fg: 'oklch(0.42 0.12 50)',  glyph: '✂' },
  };
  const c = colors[subject] || colors.math;
  const isLetter = /^[A-Za-zА-Яа-я0-9∑]+$/.test(c.glyph);
  return (
    <span
      className="pc-lesson__subj-icon"
      style={{
        background: c.bg, color: c.fg,
        width: size, height: size,
        fontSize: size * 0.45, fontWeight: 700,
      }}
    >
      {isLetter ? c.glyph : <span style={{ fontSize: size * 0.55 }}>{c.glyph}</span>}
    </span>
  );
}

// ── Бейдж оценки / отметки посещаемости ─────────────────────────────
export function MarkBadge({ mark }) {
  if (!mark) return null;
  if (mark.attendance === 'late') {
    return <span className="pc-mark pc-mark--late">+{mark.lateMinutes}</span>;
  }
  if (mark.attendance === 'absent') {
    return <span className="pc-mark pc-mark--absent">Н</span>;
  }
  if (mark.kind === 'letter') {
    return <span className={'pc-mark pc-mark--' + mark.value}>{mark.value}</span>;
  }
  if (mark.kind === 'grade') {
    return <span className={'pc-mark pc-mark--g' + mark.value}>{mark.value}</span>;
  }
  return null;
}

// ── Приветствие на главной ──────────────────────────────────────────
export function PCWelcome({ parentName, child }) {
  // «Ольга» из «Соколова Ольга Игоревна» — второе слово, или первое если
  // оно одно. Дизайн-макет всегда использует имя.
  const firstName = (parentName || '').trim().split(/\s+/);
  const greetTo = firstName.length >= 2 ? firstName[1] : firstName[0];
  return (
    <div className="pc-hero__welcome">
      <div className="pc-hero__greet">
        Добрый день{greetTo ? ', ' + greetTo : ''} 👋
      </div>
      <div className="pc-hero__line">
        Сегодня у {child.shortName} {child.todayHomework} новых ДЗ
        <br />
        и {child.nextLesson.subject.toLowerCase()} в {child.nextLesson.time}
      </div>
    </div>
  );
}

// ── Топ-шапка десктопа ──────────────────────────────────────────────
export function PCHeader({ title, sub }) {
  return (
    <div className="pc-head">
      <div>
        <div className="pc-head__title">{title}</div>
        {sub && <div className="pc-head__sub">{sub}</div>}
      </div>
      <div className="pc-head__spacer" />
      <button type="button" className="pc-head__icon-btn">
        <Icon name="search" size={16} />
      </button>
      <button type="button" className="pc-head__icon-btn">
        <Icon name="bell" size={16} />
        <span className="pc-dot" />
      </button>
    </div>
  );
}

// ── Полоса детей (десктоп) ──────────────────────────────────────────
export function PCChildBar({ children, activeId, onChoose }) {
  return (
    <div className="pc-childbar">
      {children.map((c) => (
        <div
          key={c.id}
          className={'pc-child' + (c.id === activeId ? ' is-active' : '')}
          onClick={() => onChoose && onChoose(c.id)}
        >
          <div className="pc-child__photo" style={{ background: c.photoColor }}>
            {c.firstName[0]}
          </div>
          <div>
            <div className="pc-child__name">{c.firstName}</div>
            <div className="pc-child__meta">{c.class} · {c.age} лет</div>
          </div>
          <span className={'pc-child__pay pc-child__pay--' + c.paymentStatus}>
            {c.paymentStatus === 'debt' ? 'долг' : 'оплачено'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Переключатель учебных годов (десктоп) ───────────────────────────
export function PCYearBar({ years, activeYear, onChoose }) {
  const active = years.find((y) => y.id === activeYear) || years[0];
  if (!active) return null;
  const idx = years.findIndex((y) => y.id === activeYear);
  const prev = years[idx - 1];
  const next = years[idx + 1];
  // «Архив» — только реально прошедшие годы. Будущий (upcoming) год летом
  // перед 1 сентября — не архив, а «скоро».
  const isArchive = !active.current && !active.upcoming;
  return (
    <div className="pc-yearbar">
      <span className="pc-yearbar__label">Учебный год</span>
      <div className="pc-yearbar__nav">
        <button
          type="button"
          className="pc-yearbar__btn"
          disabled={!prev}
          onClick={() => prev && onChoose(prev.id)}
          title={prev ? prev.label : 'Раньше нет'}
        >
          <Icon name="chevron-left" size={14} />
        </button>
        <div className="pc-yearbar__current">
          <b>{active.label}</b>
          <span className={'pc-yearbar__chip pc-yearbar__chip--' + (isArchive ? 'archive' : 'current')}>
            {active.current ? 'сейчас' : (active.upcoming ? 'скоро' : 'архив')}
          </span>
        </div>
        <button
          type="button"
          className="pc-yearbar__btn"
          disabled={!next}
          onClick={() => next && onChoose(next.id)}
          title={next ? next.label : 'Позже нет'}
        >
          <Icon name="chevron" size={14} />
        </button>
      </div>
      {isArchive && (
        <span className="pc-yearbar__hint">
          Просмотр архивных данных · класс {active.classLabel}
        </span>
      )}
    </div>
  );
}

// Архивный баннер.
export function PCArchiveBanner({ year }) {
  return (
    <div className="pc-archive-banner">
      <span className="pc-archive-banner__icon">i</span>
      <span>
        Вы смотрите архив за <b>{year.label}</b>. Класс — {year.classLabel}. Данные доступны только для чтения.
      </span>
    </div>
  );
}
